import React from 'react';
import { motion } from 'framer-motion';
import { Star, Quote, ExternalLink } from 'lucide-react';

const Testimonials = () => {
  const testimonials = [
    {
      name: 'Rufiya Blank',
      position: 'VP New Business Development',
      company: 'Kompass-North America',
      companyUrl: 'https://www.solutions.kompass.com/',
      content: 'Thank you Local Finder for the amazing content marketing you\'ve done for Kompass North America! We are very happy with the blog articles and the email automation process. Our marketing campaigns have been much more successful with your help.',
      rating: 5,
      highlight: 'Email Automation Success'
    },
    {
      name: 'Geeta Brown',
      position: 'President & CEO',
      company: 'InterCoast.edu',
      companyUrl: 'https://www.intercoast.edu/',
      content: 'Local Finder is a great company with caring people who treat each business as if it was their own. They are truly knowledgeable and deeply care about bringing success to the company. I am grateful for their dedication and honesty and would highly recommend them!',
      rating: 5,
      highlight: 'Exceptional Service'
    }
  ];

  const additionalTestimonials = [
    {
      name: 'TendersPage Owner',
      position: 'Business Owner',
      company: 'TendersPage',
      content: 'A big thank you to the local finder teams. Their work allowed us to access front page of google in less than 6 months.',
      rating: 5,
      highlight: 'Google Front Page Results'
    },
    {
      name: 'Jeff',
      position: 'Business Owner',
      company: 'Local Business',
      content: 'I had worked with two other marketing firms... So far, I\'ve been with them for a little over two months and I can see an immediate change in my company\'s branding and online presence. They are truly knowledgeable in what they do and sincerely put in the effort to deliver on their promises. Highly Recommended!!!',
      rating: 5,
      highlight: 'Immediate Results'
    },
    {
      name: 'Kevin',
      position: 'Business Partner',
      company: 'OTTO',
      content: 'We love working with Local Finder LLC. Both Patrick and Thomas are extremely knowledgeable and readily accessible. They are pros and know what they are doing. We rely on their knowledge for SEO and online marketing. In a digital age like today, having Local Finder on your side is an essential part of running a successful business.',
      rating: 5,
      highlight: 'Essential Partnership'
    },
    {
      name: 'Business Owner',
      position: 'Company Owner',
      company: 'Local Enterprise',
      content: 'Local Finder has a unique way of getting your business on the first page of Google. They do not over sell you and can help you with many other aspects of your business as well. I am absolutely satisfied with their service.',
      rating: 5,
      highlight: 'Google First Page'
    }
  ];

  const allTestimonials = [...testimonials, ...additionalTestimonials];

  return (
    <section id="results" className="section-padding bg-neutral-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center space-x-2 bg-accent-100 rounded-full px-4 py-2 mb-6"
          >
            <Star className="text-accent-600" size={16} />
            <span className="text-accent-700 text-sm font-medium">Client Success Stories</span>
          </motion.div>
          
          <h2 className="text-4xl lg:text-5xl font-black text-neutral-900 mb-6">
            Real Results from
            <span className="text-gradient block">Real Businesses</span>
          </h2>
          
          <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
            Don't just take our word for it. See how Local Finder LLC has transformed 
            businesses across industries with our proven marketing strategies and AI automation.
          </p>
        </motion.div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {allTestimonials.map((testimonial, index) => (
            <motion.div
              key={`${testimonial.name}-${index}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -5 }}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-neutral-200 relative"
            >
              {/* Quote Icon */}
              <div className="absolute top-6 right-6 opacity-10">
                <Quote size={48} className="text-primary-600" />
              </div>

              {/* Highlight Badge */}
              <div className="mb-6">
                <span className="bg-primary-100 text-primary-700 text-xs font-semibold px-3 py-1 rounded-full">
                  {testimonial.highlight}
                </span>
              </div>

              {/* Rating */}
              <div className="flex items-center space-x-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} size={16} className="text-yellow-400 fill-current" />
                ))}
              </div>

              {/* Content */}
              <blockquote className="text-neutral-700 text-lg leading-relaxed mb-6 relative z-10">
                "{testimonial.content}"
              </blockquote>

              {/* Author */}
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-neutral-900">{testimonial.name}</div>
                  <div className="text-sm text-neutral-600">{testimonial.position}</div>
                  <div className="text-sm font-medium text-primary-600">{testimonial.company}</div>
                </div>
                {testimonial.companyUrl && (
                  <motion.a
                    href={testimonial.companyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-700 transition-colors duration-300"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <ExternalLink size={20} />
                  </motion.a>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Results Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-primary-600 to-accent-600 rounded-3xl p-12 text-white text-center"
        >
          <h3 className="text-3xl font-bold mb-6">Join Hundreds of Satisfied Clients</h3>
          <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
            Ready to see similar results for your business? Let's discuss how our proven 
            strategies can drive growth and success for your local business.
          </p>
          
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="text-4xl font-bold mb-2">6 Months</div>
              <div className="text-lg opacity-90">To Google Front Page</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">100%</div>
              <div className="text-lg opacity-90">Client Satisfaction Rate</div>
            </div>
            <div>
              <div className="text-4xl font-bold mb-2">24/7</div>
              <div className="text-lg opacity-90">AI Automation Support</div>
            </div>
          </div>

          <motion.a
            href="#contact"
            className="inline-flex items-center space-x-2 bg-white text-primary-600 px-8 py-4 rounded-xl font-semibold hover:bg-neutral-100 transition-colors duration-300"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <span>Start Your Success Story</span>
            <Star size={20} />
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;